import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut, 
  onAuthStateChanged,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential
} from 'firebase/auth';
import { doc, getDoc, setDoc, query, where, getDocs, collection, updateDoc } from 'firebase/firestore';
import { auth, db } from './firebase';

// Auth específico para clientes (usando um auth separado)
export const customerAuthService = {
  // Registrar novo cliente
  async registerCustomer(customerData, password) {
    try {
      const { email, cpf, phone, name, companyId } = customerData;
      
      // Verificar se cliente existe na empresa
      const existingCustomer = await this.findCustomerByDocument(cpf, companyId);
      if (!existingCustomer) {
        throw new Error('Cliente não encontrado na empresa. Entre em contato com a empresa para se cadastrar primeiro.');
      }
      
      // Verificar se já tem conta
      if (existingCustomer.hasAccount) {
        throw new Error('Este cliente já possui uma conta. Use a opção "Entrar".');
      }
      
      // Criar conta Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Criar documento do cliente autenticado
      await setDoc(doc(db, 'customer_accounts', user.uid), {
        customerId: existingCustomer.id,
        companyId: companyId,
        email: email,
        name: name,
        cpf: cpf,
        phone: phone,
        createdAt: new Date().toISOString(),
        lastLogin: new Date().toISOString(),
        active: true
      });
      
      // Marcar que o cliente tem conta
      await this.updateCustomerAccountStatus(existingCustomer.id, true, user.uid);
      
      return {
        uid: user.uid,
        customerId: existingCustomer.id,
        companyId: companyId,
        email: email,
        name: name
      };
    } catch (error) {
      throw new Error(error.message);
    }
  },

  // Login do cliente
  async loginCustomer(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Buscar dados da conta do cliente
      const accountDoc = await getDoc(doc(db, 'customer_accounts', user.uid));
      
      if (!accountDoc.exists()) {
        throw new Error('Conta não encontrada');
      }
      
      const accountData = accountDoc.data();
      
      // Verificar se conta está ativa
      if (!accountData.active) {
        await signOut(auth);
        throw new Error('Sua conta está desabilitada. Entre em contato com a empresa.');
      }
      
      // Atualizar último login
      await updateDoc(doc(db, 'customer_accounts', user.uid), {
        lastLogin: new Date().toISOString()
      });
      
      return {
        uid: user.uid,
        customerId: accountData.customerId,
        companyId: accountData.companyId,
        email: accountData.email,
        name: accountData.name,
        ...accountData
      };
    } catch (error) {
      throw new Error(error.message);
    }
  },

  // Logout do cliente
  async logoutCustomer() {
    try {
      await signOut(auth);
    } catch (error) {
      throw new Error(error.message);
    }
  },

  // Login com documento (CPF/CNPJ) e senha
  async loginWithDocument(document, password) {
    try {
      // Primeiro, encontrar todas as contas de autenticação para este documento
      const cleanDocument = document.replace(/[^\d]/g, '');
      
      // Buscar por contas de autenticação com este documento
      const accountsRef = collection(db, 'customer_accounts');
      const accountQuery = query(
        accountsRef,
        where('document', '==', cleanDocument)
      );
      
      const accountSnapshot = await getDocs(accountQuery);
      if (accountSnapshot.empty) {
        throw new Error('Nenhuma conta encontrada para este documento. Verifique se você está cadastrado em alguma empresa.');
      }

      // Pegar a primeira conta (assumindo que o documento é único)
      const accountData = accountSnapshot.docs[0].data();
      
      // Fazer login com email e senha
      const result = await signInWithEmailAndPassword(auth, accountData.email, password);
      const user = result.user;
      
      // Verificar se conta está ativa
      if (!accountData.isActive) {
        await signOut(auth);
        throw new Error('Sua conta está desabilitada. Entre em contato com a empresa.');
      }
      
      // Atualizar último login
      await updateDoc(doc(db, 'customer_accounts', user.uid), {
        lastLogin: new Date().toISOString()
      });
      
      return {
        uid: user.uid,
        document: accountData.document,
        email: accountData.email,
        name: accountData.name,
        ...accountData
      };
    } catch (error) {
      if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        throw new Error('Senha incorreta');
      }
      throw new Error(error.message);
    }
  },

  // Registro de cliente por documento
  async registerCustomerByDocument({ document, password }) {
    try {
      const cleanDocument = document.replace(/[^\d]/g, '');
      
      // Verificar se já existe conta com este documento
      const accountsRef = collection(db, 'customer_accounts');
      const accountQuery = query(
        accountsRef,
        where('document', '==', cleanDocument)
      );
      
      const accountSnapshot = await getDocs(accountQuery);
      if (!accountSnapshot.empty) {
        throw new Error('Já existe uma conta cadastrada com este documento');
      }

      // Buscar clientes cadastrados com este documento em qualquer empresa
      const customersRef = collection(db, 'customers');
      const customerQuery = query(
        customersRef, 
        where('cpf', '==', cleanDocument)
      );
      
      const customerSnapshot = await getDocs(customerQuery);
      if (customerSnapshot.empty) {
        throw new Error('Documento não encontrado em nenhuma empresa. Entre em contato com a empresa onde você é cliente para se cadastrar primeiro.');
      }

      // Pegar dados do primeiro cliente encontrado (já que é o mesmo CPF)
      const firstCustomer = customerSnapshot.docs[0].data();
      const customerName = firstCustomer.name;
      const customerPhone = firstCustomer.phone;
      
      // Usar email do cliente se existir, senão gerar um baseado no documento
      let customerEmail = firstCustomer.email;
      if (!customerEmail) {
        // Criar email baseado no documento
        customerEmail = `cliente.${cleanDocument}@meufiel.app`;
      }

      let user;
      let isNewAuthAccount = false;

      try {
        // Tentar criar nova conta no Firebase Auth
        const userCredential = await createUserWithEmailAndPassword(auth, customerEmail, password);
        user = userCredential.user;
        isNewAuthAccount = true;
      } catch (authError) {
        if (authError.code === 'auth/email-already-in-use') {
          // Email já existe no Firebase Auth, tentar fazer login para verificar se é o mesmo usuário
          try {
            const signInResult = await signInWithEmailAndPassword(auth, customerEmail, password);
            user = signInResult.user;
            
            // Verificar se já existe registro na customer_accounts para este usuário
            const existingAccountDoc = await getDoc(doc(db, 'customer_accounts', user.uid));
            if (existingAccountDoc.exists()) {
              throw new Error('Conta já configurada. Use a opção de login.');
            }
            
            isNewAuthAccount = false;
          } catch (signInError) {
            if (signInError.code === 'auth/wrong-password' || signInError.code === 'auth/invalid-credential') {
              throw new Error('Este email já está em uso com uma senha diferente. Entre em contato com o suporte.');
            }
            throw signInError;
          }
        } else {
          throw authError;
        }
      }

      // Coletar IDs das empresas onde o cliente está cadastrado
      const companyIds = customerSnapshot.docs.map(doc => doc.data().companyId);
      const customerIds = customerSnapshot.docs.map(doc => doc.id);

      // Criar registro na coleção customer_accounts
      await setDoc(doc(db, 'customer_accounts', user.uid), {
        document: cleanDocument,
        email: customerEmail,
        name: customerName,
        phone: customerPhone,
        companyIds: companyIds, // Array com IDs de todas as empresas
        customerIds: customerIds, // Array com IDs dos registros de cliente
        isActive: true,
        createdAt: new Date().toISOString(),
        lastLogin: new Date().toISOString()
      });

      return {
        uid: user.uid,
        document: cleanDocument,
        email: customerEmail,
        name: customerName,
        phone: customerPhone,
        companyIds: companyIds,
        customerIds: customerIds,
        isNewAuthAccount: isNewAuthAccount
      };
    } catch (error) {
      if (error.code === 'auth/weak-password') {
        throw new Error('A senha deve ter pelo menos 6 caracteres');
      }
      throw new Error(error.message);
    }
  },

  // Verificar se cliente existe por documento
  async findCustomerByDocument(document, companyId) {
    try {
      const cleanDocument = document.replace(/[^\d]/g, '');
      const customersRef = collection(db, 'customers');
      const q = query(
        customersRef, 
        where('cpf', '==', cleanDocument),
        where('companyId', '==', companyId)
      );
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        return null;
      }
      
      return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
    } catch (error) {
      console.error('Erro ao buscar cliente:', error);
      return null;
    }
  },

  // Atualizar status de conta do cliente
  async updateCustomerAccountStatus(customerId, hasAccount, authUid = null) {
    try {
      const updateData = { 
        hasAccount: hasAccount,
        updatedAt: new Date().toISOString()
      };
      
      if (authUid) {
        updateData.authUid = authUid;
      }
      
      await updateDoc(doc(db, 'customers', customerId), updateData);
    } catch (error) {
      console.error('Erro ao atualizar status da conta:', error);
    }
  },

  // Verificar estado de autenticação
  onAuthStateChange(callback) {
    return onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const accountDoc = await getDoc(doc(db, 'customer_accounts', user.uid));
          if (accountDoc.exists()) {
            const accountData = accountDoc.data();
            callback({
              uid: user.uid,
              email: user.email,
              ...accountData
            });
          } else {
            callback(null);
          }
        } catch (error) {
          console.error('Erro ao verificar conta:', error);
          callback(null);
        }
      } else {
        callback(null);
      }
    });
  },

  // Alterar senha
  async changePassword(currentPassword, newPassword) {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Usuário não autenticado');
      }
      
      // Verificar senha atual
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      
      // Atualizar senha
      await updatePassword(user, newPassword);
    } catch (error) {
      throw new Error('Erro ao alterar senha: ' + error.message);
    }
  },

  // Obter cliente atual autenticado
  async getCurrentCustomer() {
    try {
      const user = auth.currentUser;
      if (!user) return null;
      
      const accountDoc = await getDoc(doc(db, 'customer_accounts', user.uid));
      if (!accountDoc.exists()) return null;
      
      return { uid: user.uid, email: user.email, ...accountDoc.data() };
    } catch (error) {
      console.error('Erro ao obter cliente atual:', error);
      return null;
    }
  }
};
